import { Injectable } from "@angular/core";

import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { FolderModel, TreeNode, TreeNodeUnion } from "@documentation/models/folder.model";
import { BaseHttpService } from "@shared/services/base-http.service";

import {
  DocumentListItemModel,
  DocumentModel,
  DocumentVersionListItemModel,
  DocumentVersionModel
} from "../models/document.model";

@Injectable({
  providedIn: "root"
})
export class DocumentHttpService extends BaseHttpService {
  protected override baseEndpoint = "documents";

  getDocumentListItems(): Observable<FolderModel> {
    return this.get<FolderModel>({
      endpoint: "",
      type: FolderModel
    });
  }

  searchDocuments(searchQuery: string): Observable<TreeNode[]> {
    const params = { search: searchQuery };
    return this.getManyWithoutPagination<TreeNode>({ endpoint: "", options: { params } }).pipe(
      map((nodes) => TreeNodeUnion.fromPlain(nodes))
    );
  }

  getPublicDocumentListItem(documentId: string): Observable<DocumentListItemModel> {
    return this.get<DocumentListItemModel>({
      endpoint: documentId,
      type: DocumentListItemModel
    });
  }

  getSharedDocuments(searchQuery?: string): Observable<DocumentListItemModel[]> {
    const params = searchQuery ? { search: searchQuery } : undefined;
    return this.getManyWithoutPagination<DocumentListItemModel>({
      endpoint: "shared-with-me",
      type: DocumentListItemModel,
      options: { params }
    });
  }

  getInvites(): Observable<any[]> {
    return this.get<any[]>({ endpoint: `shares/invites` });
  }

  getDocument(documentId: string): Observable<DocumentModel> {
    return this.get<DocumentModel>({
      endpoint: documentId,
      type: DocumentModel
    });
  }

  getPublicStatus(documentId: string): Observable<{ isPublic: boolean }> {
    return this.get<{ isPublic: boolean }>({
      endpoint: `${documentId}/public-status`
    });
  }

  getListItem(documentId: string): Observable<DocumentListItemModel> {
    return this.get<DocumentListItemModel>({
      endpoint: `${documentId}/list-item`,
      type: DocumentListItemModel
    });
  }

  getDocumentVersions(documentId: string): Observable<DocumentVersionListItemModel[]> {
    return this.getManyWithoutPagination<DocumentVersionListItemModel>({
      endpoint: `${documentId}/versions`,
      type: DocumentVersionListItemModel
    });
  }

  getDocumentVersion(documentId: string, version: number): Observable<DocumentVersionModel> {
    return this.get<DocumentVersionModel>({
      endpoint: `${documentId}/versions/${version}`,
      type: DocumentVersionModel
    });
  }

  createDocument(data: { title: string; content?: string; isNewUser?: boolean; folderId?: string }): Observable<DocumentModel> {
    return this.post<DocumentModel>({
      endpoint: "",
      body: data,
      type: DocumentModel
    });
  }

  duplicateDocument(documentId: string, data: { newTitle: string }): Observable<DocumentModel> {
    return this.post<DocumentModel>({
      endpoint: `${documentId}/duplicate`,
      body: data,
      type: DocumentModel
    });
  }

  updateDocumentTitle(documentId: string, title: string): Observable<DocumentModel> {
    return this.patch<DocumentModel>({
      endpoint: `${documentId}/title`,
      body: { title },
      type: DocumentModel
    });
  }

  updateDocumentContent(documentId: string, content: string): Observable<DocumentModel> {
    return this.post<DocumentModel>({
      endpoint: `${documentId}/content`,
      body: { content },
      type: DocumentModel
    });
  }

  deleteDocument(documentId: string): Observable<{ id: string }> {
    return this.delete<{ id: string }>({
      endpoint: documentId
    });
  }

  updatePositions(updates: { id: string; position: number; type: string }[]): Observable<void> {
    return this.put<void>({
      endpoint: `positions`,
      body: updates
    });
  }

  getMyPublicDocuments(searchQuery?: string): Observable<TreeNode[]> {
    const params = searchQuery ? { search: searchQuery } : undefined;
    return this.getManyWithoutPagination<DocumentListItemModel>({
      endpoint: `public`,
      options: { params }
    }).pipe(map((nodes) => TreeNodeUnion.fromPlain(nodes)));
  }

  getPublicDocument(id: string): Observable<DocumentModel> {
    return this.get<DocumentModel>({
      endpoint: `public/${id}`,
      type: DocumentModel,
      showToast: false
    });
  }

  setPublic(documentId: string, isPublic: boolean): Observable<DocumentModel> {
    return this.patch<DocumentModel>({
      endpoint: `${documentId}/public`,
      body: { isPublic },
      type: DocumentModel
    });
  }

  updateAiModel(documentId: string, aiModelId: string): Observable<DocumentModel> {
    return this.patch<DocumentModel>({
      endpoint: `${documentId}/ai-model`,
      body: { aiModelId },
      type: DocumentModel
    });
  }

  uploadDocumentImage(documentId: string, file: File): Observable<{ url: string }> {
    const form = new FormData();
    form.append("file", file);
    return this.post<{ url: string }>({
      endpoint: `${documentId}/images`,
      body: form,
      // Avoid default JSON headers; HttpClient sets multipart automatically when FormData is used
      options: {
        headers: {}
      }
    });
  }

  deleteDocumentImage(documentId: string, objectKey: string): Observable<{ success: true }> {
    return this.delete<{ success: true }>({
      endpoint: `${documentId}/images`,
      options: {
        // Some backends prefer DELETE with body; Angular HttpClient supports it via options.body
        // Our backend uses @Body("objectKey") so send in body
        body: { objectKey } as any
      } as any
    });
  }

  moveDocumentToFolder(documentId: string, newFolderId: string, position?: number): Observable<void> {
    return this.patch<void>({
      endpoint: `${documentId}/move`,
      body: { newFolderId, position }
    });
  }
}
